/**
 * Resolves the asset paths the mock seed carries into URLs a browser can load.
 *
 * The seed stores iOS asset-catalogue paths ("Characters/alex/alex-1") because
 * it was ported verbatim from the Swift mocks. Rewriting the seed to hold web
 * URLs would have made it undiffable against its source, so the translation
 * lives here instead.
 *
 * Two renditions exist per image (see scripts that build `public/characters`):
 * a full one capped at 900px for media, and a 256px square for avatars.
 */

/** True for the iOS asset-catalogue paths the seed uses. */
function isCharacterPath(path: string): boolean {
  return path.startsWith("Characters/");
}

export type AssetSize = "full" | "thumb";

/**
 * Media URL for a seeded asset path. Absolute URLs (picsum thumbnails, CDN
 * placeholders) pass through untouched.
 */
export function assetUrl(path: string | undefined, size: AssetSize = "full"): string | undefined {
  if (!path) return undefined;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (!isCharacterPath(path)) return path;

  const relative = path.slice("Characters/".length);
  const suffix = size === "thumb" ? "@thumb" : "";
  return `/characters/${relative}${suffix}.webp`;
}

/** Avatar URL — always the square rendition. */
export function avatarUrl(path: string | undefined): string | undefined {
  return assetUrl(path, "thumb");
}

/**
 * Initials for the avatar fallback.
 *
 * Photo-first; initials on a pastel fill are what shows when someone has no
 * photo. That is a real state in the seed, not a synthetic one — Ethan Nguyen
 * has no portrait, and neither do professors or outside reviewers.
 */
export function initials(displayName: string): string {
  const parts = displayName
    .replace(/^(Dr|Prof)\.\s*/i, "")
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Deterministic pastel fill for an initials avatar.
 *
 * Hue is derived from the id so a person keeps the same colour everywhere they
 * appear. Saturation and lightness are fixed low so the chip never competes
 * with the mint action colour or reads as a status.
 */
export function avatarTint(seed: string): { background: string; color: string } {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) % 360;
  }
  return {
    background: `hsl(${h} 46% 88%)`,
    color: `hsl(${h} 42% 30%)`,
  };
}
